import { useState, useEffect } from 'react'
import axios from 'axios'
import { Box, Stack, Pagination, Typography, FormControl, InputLabel, Select, MenuItem } from '@mui/material';

const useFetchDataWithPagination = (url) => {

  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const fetchPage = (page = 1, per_page = perPage, requestUrl = null) => {
    setIsLoading(true);
    const abortConnection = new AbortController();
    const token = localStorage.getItem('token');

    const separator = (requestUrl || url).includes('?') ? '&' : '?';
    const fullUrl = `${requestUrl || url}${separator}page=${page}&per_page=${per_page}`;

    return axios
      .get(fullUrl, {
        signal: abortConnection.signal,
        headers: {
          Authorization: 'Bearer ' + token,
        },
      })
      .then((response) => {
        setData(response);
        setError(null);
        setCurrentPage(response?.data?.data?.current_page ?? page);
      })
      .catch((err) => {
        console.error(err.message || err);
        setError(err.message || err);
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    fetchPage(1, perPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, perPage]);

  const changePage = (arg) => {
    // arg can be event from MUI Pagination (e, value) or react-paginate format { selected }
    let pageNumber = 1;
    if (typeof arg === 'object' && arg.selected !== undefined) {
      pageNumber = arg.selected + 1; // react-paginate 0-based
    } else if (typeof arg === 'number') {
      pageNumber = arg;
    } else if (Array.isArray(arg) && arg.length > 0) {
      pageNumber = arg[0];
    }

    setCurrentPage(pageNumber);
    return fetchPage(pageNumber, perPage);
  };

  const searchIntoDatabase = (searchQuery) => {
    // searchQuery is expected to be a relative path like 'users/search/term' or full URL
    setCurrentPage(1);
    return fetchPage(1, perPage, searchQuery);
  };

  const refreshSearch = () => {
    return fetchPage(currentPage, perPage);
  };

  const handlePerPageChange = (newPerPage) => {
    setPerPage(newPerPage);
    setCurrentPage(1);
    return fetchPage(1, newPerPage);
  };

  const pageCount = Math.ceil((data?.data?.total ?? 0) / (data?.data?.per_page ?? perPage));

  const paginate = () => {
    if (!data?.data) return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="body2">Aucun résultat ou en cours de chargement...</Typography>
      </Box>
    );

    // Always show the pagination bar and controls (even when only 1 page) so it's visible for debugging / UX
    const effectivePerPage = data?.data?.per_page ?? perPage;
    const effectiveTotal = data?.data?.total ?? 0;
    const effectivePageCount = Math.max(1, Math.ceil(effectiveTotal / effectivePerPage));

    return (
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Pagination
            count={effectivePageCount}
            page={currentPage}
            onChange={(e, value) => changePage(value)}
            color="primary"
            showFirstButton
            showLastButton
            siblingCount={1}
            boundaryCount={1}
            aria-label="pagination"
          />
          <Typography variant="body2">Page {currentPage} sur {effectivePageCount}</Typography>
        </Stack>

        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="body2">Total: {effectiveTotal}</Typography>
          <FormControl size="small">
            <InputLabel id="rows-per-page-label">Par page</InputLabel>
            <Select
              labelId="rows-per-page-label"
              id="rows-per-page"
              value={effectivePerPage}
              label="Par page"
              onChange={(e) => handlePerPageChange(Number(e.target.value))}
            >
              <MenuItem value={5}>5</MenuItem>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Box>
    );
  };

  const filterData = (query, dataArray) => {
    if (!query) return dataArray;
    return dataArray.filter((d) => JSON.stringify(d).toLowerCase().includes(query));
  };

  return { data, isLoading, error, paginate, searchIntoDatabase, refreshSearch, filterData, changePage, currentPage, perPage };
};

export default useFetchDataWithPagination;